import { useMutation, useQueryClient } from '@tanstack/react-query'
import { App as AntdApp, Modal, Typography } from 'antd'
import { useTranslation } from 'react-i18next'

import { organizationsUserGroupsDestroyMutation } from 'src/api/main'
import type { UserGroup } from 'src/api/main'
import { useShowApiErrors } from 'src/utils'

import { invalidateWorkspaceUserGroups } from '../hooks'

const { Text } = Typography

type Props = {
  workspaceId: number
  open: boolean
  group: UserGroup | null
  onClose: () => void
}

export const UserGroupDeleteModal = ({
  workspaceId,
  open,
  group,
  onClose
}: Props) => {
  const { t } = useTranslation()
  const { message } = AntdApp.useApp()
  const qc = useQueryClient()
  const showApiErrors = useShowApiErrors()

  const { mutateAsync, isPending } = useMutation({
    ...organizationsUserGroupsDestroyMutation(),
    onSuccess: async () => {
      await invalidateWorkspaceUserGroups(qc, workspaceId)
      message.success(t('users.groups.delete.success'))
      onClose()
    },
    onError: (e) => showApiErrors(e)
  })

  return (
    <Modal
      open={open}
      centered
      title={t('users.groups.delete.title')}
      okText={t('users.groups.delete.submit')}
      cancelText={t('users.groups.form.cancel')}
      okType="danger"
      okButtonProps={{ loading: isPending }}
      cancelButtonProps={{ disabled: isPending }}
      onOk={() => {
        if (group) void mutateAsync({ path: { id: group.id } })
      }}
      onCancel={() => {
        if (!isPending) onClose()
      }}
      destroyOnHidden
    >
      <Text type="secondary">
        {t('users.groups.delete.description', { name: group?.name ?? '' })}
      </Text>
    </Modal>
  )
}
